/**
 * Per-section property editor used by AdminHomeBuilder.
 * Renders different fields based on section.type.
 */
import { useEffect, useState, useMemo } from 'react';
import api from '../../../services/api';
import { Trash2, Plus, X } from 'lucide-react';
import HeroOverlayEditor from './HeroOverlayEditor';

const Field = ({ label, children, hint }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-medium text-slate-600 uppercase tracking-wide">{label}</label>
    {children}
    {hint && <p className="text-[11px] text-slate-400">{hint}</p>}
  </div>
);

const Input = (props) => (
  <input
    {...props}
    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]/30"
  />
);

const Textarea = (props) => (
  <textarea
    {...props}
    rows={props.rows || 3}
    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]/30"
  />
);

const Select = ({ children, ...props }) => (
  <select
    {...props}
    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]/30"
  >
    {children}
  </select>
);

const ColorInput = ({ value, onChange }) => (
  <div className="flex items-center gap-2">
    <input
      type="color"
      value={value || '#ffffff'}
      onChange={(e) => onChange(e.target.value)}
      className="w-12 h-9 rounded border border-slate-200 cursor-pointer"
    />
    <Input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)} />
  </div>
);

// ─────────────────── Common layout settings (every section) ───────────────────
const LayoutSettings = ({ section, update }) => (
  <div className="grid grid-cols-2 gap-3 pt-4 border-t border-slate-100">
    <Field label="Höjd">
      <Select value={section.height || 'medium'} onChange={(e) => update('height', e.target.value)}>
        <option value="small">Liten</option>
        <option value="medium">Mellan</option>
        <option value="large">Stor</option>
        <option value="full">Hel skärm</option>
      </Select>
    </Field>
    <Field label="Padding">
      <Select value={section.padding || 'large'} onChange={(e) => update('padding', e.target.value)}>
        <option value="none">Ingen</option>
        <option value="small">Liten</option>
        <option value="medium">Mellan</option>
        <option value="large">Stor</option>
      </Select>
    </Field>
    <Field label="Bakgrundsfärg">
      <ColorInput value={section.bg_color} onChange={(v) => update('bg_color', v)} />
    </Field>
  </div>
);

// ─────────────────── Hero editor ───────────────────
const HeroEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Förrubrik (eyebrow)">
      <Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} placeholder="Personliga produkter" />
    </Field>
    <Field label="Huvudrubrik">
      <Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} />
    </Field>
    <Field label="Markerat ord (färgas i orange)" hint="Detta ord eller frasen ska finnas i huvudrubriken">
      <Input value={section.heading_highlight || ''} onChange={(e) => update('heading_highlight', e.target.value)} placeholder="dina bilder" />
    </Field>
    <Field label="Underrubrik">
      <Textarea value={section.subheading || ''} onChange={(e) => update('subheading', e.target.value)} />
    </Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Primär knapp text">
        <Input value={section.primary_cta_label || ''} onChange={(e) => update('primary_cta_label', e.target.value)} />
      </Field>
      <Field label="Primär knapp länk">
        <Input value={section.primary_cta_link || ''} onChange={(e) => update('primary_cta_link', e.target.value)} placeholder="/produkter" />
      </Field>
      <Field label="Sekundär knapp text">
        <Input value={section.secondary_cta_label || ''} onChange={(e) => update('secondary_cta_label', e.target.value)} />
      </Field>
      <Field label="Sekundär knapp länk">
        <Input value={section.secondary_cta_link || ''} onChange={(e) => update('secondary_cta_link', e.target.value)} placeholder="/produkter" />
      </Field>
    </div>
    <Field label="Bakgrundsbild URL">
      <Input value={section.bg_image || ''} onChange={(e) => update('bg_image', e.target.value)} placeholder="https://..." />
    </Field>
    <Field label="Bakgrunds-overlay opacitet (0-1)" hint="Hur mycket vit slöja över bakgrundsbilden">
      <Input
        type="number" step="0.1" min="0" max="1"
        value={section.bg_overlay_opacity ?? 0.4}
        onChange={(e) => update('bg_overlay_opacity', parseFloat(e.target.value))}
      />
    </Field>
    <Field label="Sidobild URL (visas till höger)">
      <Input value={section.side_image || ''} onChange={(e) => update('side_image', e.target.value)} />
    </Field>
    <Field label="Badge-text">
      <Input value={section.badge_text || ''} onChange={(e) => update('badge_text', e.target.value)} placeholder="500+ nöjda kunder" />
    </Field>
    <HeroOverlayEditor section={section} update={update} />
  </div>
);

// ─────────────────── Featured Products editor ───────────────────
const FeaturedProductsEditor = ({ section, update }) => {
  const [allProducts, setAllProducts] = useState([]);

  useEffect(() => {
    api.get('/products').then((res) => setAllProducts(res.data || [])).catch(() => {});
  }, []);

  const productIds = section.product_ids || [];
  // Memoize the "available products" filter so we don't recompute on every render.
  // We reference section.product_ids directly (not the local fallback) so the deps array is stable.
  const availableProducts = useMemo(
    () => {
      const ids = section.product_ids || [];
      return allProducts.filter((p) => !ids.includes(p.product_id));
    },
    [allProducts, section.product_ids]
  );

  const addProduct = (pid) => {
    if (!productIds.includes(pid)) {
      update('product_ids', [...productIds, pid]);
    }
  };

  const removeProduct = (pid) => {
    update('product_ids', productIds.filter((p) => p !== pid));
  };

  return (
    <div className="space-y-3">
      <Field label="Förrubrik">
        <Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} />
      </Field>
      <Field label="Rubrik">
        <Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Läge">
          <Select value={section.mode || 'auto'} onChange={(e) => update('mode', e.target.value)}>
            <option value="auto">Automatisk</option>
            <option value="manual">Manuell (välj produkter)</option>
          </Select>
        </Field>
        <Field label="Antal produkter">
          <Input
            type="number" min="1" max="12"
            value={section.count || 4}
            onChange={(e) => update('count', parseInt(e.target.value, 10) || 4)}
          />
        </Field>
      </div>

      {section.mode === 'auto' ? (
        <Field label="Automatisk strategi">
          <Select value={section.auto_strategy || 'newest'} onChange={(e) => update('auto_strategy', e.target.value)}>
            <option value="newest">Nyaste först</option>
            <option value="popular">Mest sålda</option>
            <option value="featured">Utvalda (admin-markerade)</option>
          </Select>
        </Field>
      ) : (
        <Field label="Välj produkter manuellt" hint={`${productIds.length} valda. Klicka för att lägga till/ta bort.`}>
          <div className="space-y-2">
            {productIds.length > 0 && (
              <div className="flex flex-wrap gap-2 p-2 bg-slate-50 rounded-lg">
                {productIds.map((pid) => {
                  const p = allProducts.find((x) => x.product_id === pid);
                  return (
                    <span key={pid} className="inline-flex items-center gap-1 px-2 py-1 bg-white border border-slate-200 rounded text-xs">
                      {p?.name || pid}
                      <button onClick={() => removeProduct(pid)} className="hover:text-red-500">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <Select value="" onChange={(e) => e.target.value && addProduct(e.target.value)}>
              <option value="">+ Lägg till produkt...</option>
              {availableProducts.map((p) => (
                <option key={p.product_id} value={p.product_id}>
                  {p.name} ({(p.price || 0)} kr)
                </option>
              ))}
            </Select>
          </div>
        </Field>
      )}
    </div>
  );
};

// ─────────────────── Three Steps editor ───────────────────
const ICON_OPTIONS = ['Upload', 'Palette', 'ShoppingBag', 'Gift', 'Truck', 'Shield', 'Heart', 'Sparkles', 'Zap'];

const ThreeStepsEditor = ({ section, update }) => {
  const steps = section.steps || [];
  // Ensure each step has a stable unique key for React reconciliation
  const ensureKey = (s, idx) => (s._key ? s : { ...s, _key: `step-${Date.now()}-${idx}-${Math.random().toString(36).slice(2, 8)}` });
  const updateStep = (idx, field, val) => {
    const next = steps.map((s, i) => (i === idx ? { ...ensureKey(s, i), [field]: val } : s));
    update('steps', next);
  };
  const addStep = () => update('steps', [...steps, ensureKey({ icon: 'Upload', title: 'Nytt steg', description: '' }, steps.length)]);
  const removeStep = (idx) => update('steps', steps.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <Field label="Förrubrik"><Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} /></Field>
      <Field label="Rubrik"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
      <div className="space-y-3 pt-2 border-t border-slate-100">
        {steps.map((step, idx) => {
          const keyed = ensureKey(step, idx);
          return (
            <div key={keyed._key} className="border border-slate-200 rounded-lg p-3 space-y-2 bg-slate-50/50">
              <div className="flex justify-between items-center">
                <span className="text-xs font-semibold text-slate-600">Steg {idx + 1}</span>
                <button onClick={() => removeStep(idx)} className="text-red-500 hover:text-red-700">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <Select value={step.icon} onChange={(e) => updateStep(idx, 'icon', e.target.value)}>
                {ICON_OPTIONS.map((ic) => <option key={ic} value={ic}>{ic}</option>)}
              </Select>
              <Input value={step.title || ''} onChange={(e) => updateStep(idx, 'title', e.target.value)} placeholder="Titel" />
              <Textarea rows={2} value={step.description || ''} onChange={(e) => updateStep(idx, 'description', e.target.value)} placeholder="Beskrivning" />
            </div>
          );
        })}
        <button onClick={addStep} className="w-full py-2 text-sm text-[#2a9d8f] border border-dashed border-[#2a9d8f]/40 rounded-lg hover:bg-[#2a9d8f]/5 flex items-center justify-center gap-2">
          <Plus className="w-4 h-4" /> Lägg till steg
        </button>
      </div>
    </div>
  );
};

// ─────────────────── Categories editor ───────────────────
const CategoriesEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Förrubrik"><Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} /></Field>
    <Field label="Rubrik"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
    <label className="flex items-center gap-2 text-sm">
      <input
        type="checkbox"
        checked={section.show_view_all_link !== false}
        onChange={(e) => update('show_view_all_link', e.target.checked)}
      />
      Visa "Visa alla"-länk
    </label>
  </div>
);

// ─────────────────── Reviews editor ───────────────────
const ReviewsEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Förrubrik"><Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} /></Field>
    <Field label="Rubrik"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
    <Field label="Antal recensioner att visa">
      <Input type="number" min="1" max="9" value={section.count || 3} onChange={(e) => update('count', parseInt(e.target.value, 10) || 3)} />
    </Field>
  </div>
);

// ─────────────────── CTA Banner editor ───────────────────
const CtaBannerEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Rubrik"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
    <Field label="Underrubrik"><Textarea value={section.subheading || ''} onChange={(e) => update('subheading', e.target.value)} /></Field>
    <div className="grid grid-cols-2 gap-3">
      <Field label="Knapp text"><Input value={section.cta_label || ''} onChange={(e) => update('cta_label', e.target.value)} /></Field>
      <Field label="Knapp länk"><Input value={section.cta_link || ''} onChange={(e) => update('cta_link', e.target.value)} placeholder="/produkter" /></Field>
    </div>
    <Field label="Korts bakgrundsfärg">
      <ColorInput value={section.card_bg_color} onChange={(v) => update('card_bg_color', v)} />
    </Field>
  </div>
);

// ─────────────────── Text Block editor ───────────────────
const TextBlockEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Förrubrik"><Input value={section.eyebrow || ''} onChange={(e) => update('eyebrow', e.target.value)} /></Field>
    <Field label="Rubrik"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
    <Field label="Brödtext (HTML stöds)" hint="Du kan använda <p>, <strong>, <a href=''> osv">
      <Textarea rows={6} value={section.body || ''} onChange={(e) => update('body', e.target.value)} />
    </Field>
    <Field label="Justering">
      <Select value={section.alignment || 'left'} onChange={(e) => update('alignment', e.target.value)}>
        <option value="left">Vänster</option>
        <option value="center">Centrerad</option>
      </Select>
    </Field>
  </div>
);

// ─────────────────── Image Banner editor ───────────────────
const ImageBannerEditor = ({ section, update }) => (
  <div className="space-y-3">
    <Field label="Bild URL"><Input value={section.image || ''} onChange={(e) => update('image', e.target.value)} /></Field>
    <Field label="Rubrik (visas över bilden)"><Input value={section.heading || ''} onChange={(e) => update('heading', e.target.value)} /></Field>
    <Field label="Underrubrik"><Input value={section.subheading || ''} onChange={(e) => update('subheading', e.target.value)} /></Field>
    <Field label="Länk vid klick"><Input value={section.link || ''} onChange={(e) => update('link', e.target.value)} placeholder="/produkter" /></Field>
  </div>
);

// ─────────────────── Dispatcher ───────────────────
const SectionEditor = ({ section, onUpdate }) => {
  const update = (field, value) => onUpdate({ ...section, [field]: value });
  const editorByType = {
    'hero': HeroEditor,
    'featured-products': FeaturedProductsEditor,
    'three-steps': ThreeStepsEditor,
    'categories': CategoriesEditor,
    'reviews': ReviewsEditor,
    'cta-banner': CtaBannerEditor,
    'text-block': TextBlockEditor,
    'image-banner': ImageBannerEditor,
  };
  const Editor = editorByType[section.type];
  if (!Editor) return <p className="text-sm text-slate-500">Okänd sektionstyp: {section.type}</p>;
  return (
    <div>
      <Editor section={section} update={update} />
      <LayoutSettings section={section} update={update} />
    </div>
  );
};

export default SectionEditor;
